import React from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  LogOut,
  MoveDown,
  MoveUp,
  Plus,
  Search,
  Settings,
  Shield,
  Trash2,
  FolderTree,
  Package,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { useContent } from "@/lib/contentStore";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { ADMIN_AUTH_API_BASE, ADMIN_BASE_PATH, ADMIN_LOGIN_PATH } from "@/lib/panelPaths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ImageManager } from "@/components/ImageManager";
import { useToast } from "@/hooks/use-toast";

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9а-яё\s-]/gi, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function buildId(prefix = "id") {
  return `${prefix}_${Math.random().toString(16).slice(2, 10)}`;
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const COMPANY_DEFAULTS = { ...COMPANY_INFO };

function SectionTitle({ title, desc }) {
  return (
    <div className="mb-4">
      <div className="eyebrow">Управление</div>
      <div className="mt-1 text-base font-semibold text-[#231f1a]">{title}</div>
      {desc ? <div className="mt-1 text-sm leading-6 text-[#5f584f]">{desc}</div> : null}
    </div>
  );
}

function ImagePicker({ value, onChange, placeholder = "Выберите изображение" }) {
  return (
    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
      <Input value={value || ""} readOnly placeholder={placeholder} className="flex-1" />
      <ImageManager onSelect={onChange} />
      {value ? (
        <Button type="button" variant="secondary" onClick={() => onChange("")} className="w-full sm:w-auto">Сброс</Button>
      ) : null}
    </div>
  );
}

const HERO_IMAGE_SPEC = {
  width: 1920,
  height: 1080,
  ratio: "16:9",
  formats: "JPG, PNG, WEBP",
};

function readImageMeta(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const extMatch = url.toLowerCase().match(/\.([a-z0-9]+)(\?|$)/);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        format: (extMatch?.[1] || "unknown").toUpperCase(),
      });
    };
    img.onerror = () => reject(new Error("Не удалось прочитать изображение"));
    img.src = url;
  });
}

function SlideImageMeta({ url }) {
  const [meta, setMeta] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let active = true;
    setMeta(null);
    setError("");

    if (!url) return () => {
      active = false;
    };

    readImageMeta(url)
      .then((next) => {
        if (active) setMeta(next);
      })
      .catch(() => {
        if (active) setError("Не удалось определить размер/формат.");
      });

    return () => {
      active = false;
    };
  }, [url]);

  if (!url) {
    return <div className="text-xs text-amber-700">Изображение не выбрано.</div>;
  }
  if (error) {
    return <div className="text-xs text-amber-700">{error}</div>;
  }
  if (!meta) {
    return <div className="text-xs text-zinc-500">Проверка изображения...</div>;
  }

  const isAllowedFormat = ["JPG", "JPEG", "PNG", "WEBP"].includes(meta.format);
  const isRecommendedSize = meta.width === HERO_IMAGE_SPEC.width && meta.height === HERO_IMAGE_SPEC.height;
  const ok = isAllowedFormat && isRecommendedSize;

  return (
    <div className={cn("text-xs", ok ? "text-emerald-700" : "text-amber-700")}>
      Текущий файл: {meta.width}x{meta.height}px, {meta.format}. Рекомендовано: {HERO_IMAGE_SPEC.width}x{HERO_IMAGE_SPEC.height}px ({HERO_IMAGE_SPEC.ratio}), {HERO_IMAGE_SPEC.formats}.
    </div>
  );
}

async function authMe() {
  await axios.get(`${ADMIN_AUTH_API_BASE}/me`, { withCredentials: true });
}

export function AdminGate({ children }) {
  const nav = useNavigate();
  const [state, setState] = React.useState("loading");

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        await authMe();
        if (active) setState("authed");
      } catch {
        if (active) setState("unauthorized");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (state === "unauthorized") {
      nav(ADMIN_LOGIN_PATH, { replace: true });
    }
  }, [state, nav]);

  if (state !== "authed") {
    return <div className="text-sm text-zinc-600">Проверка доступа...</div>;
  }
  return children;
}

export function AdminLogin() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${ADMIN_AUTH_API_BASE}/login`,
        { username, password },
        { withCredentials: true }
      );
      toast({ title: "Вход выполнен" });
      nav(ADMIN_BASE_PATH, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError("Слишком много попыток. Попробуйте позже.");
      } else {
        setError("Неверный логин или пароль");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md px-1 sm:px-0">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#2d2823]">
          <Shield className="h-4 w-4" />
          Вход в панель управления
        </div>
        <h1 className="display-title mt-2 text-4xl text-[#231f1a]">DALINI Admin</h1>
        <div className="mt-1 text-sm text-[#625a51]">Введите учетные данные администратора.</div>
      </div>

      <form onSubmit={submit} className="surface-card space-y-3 rounded-xl p-4 sm:p-6">
        <Input placeholder="Логин" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        <Input placeholder="Пароль" value={password} onChange={(e) => setPassword(e.target.value)} type="password" />
        {error ? <div className="text-sm text-red-600">{error}</div> : null}
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
          <Button type="submit" disabled={loading} className="w-full sm:w-auto">{loading ? "Вход..." : "Войти"}</Button>
          <Button asChild variant="secondary" className="w-full sm:w-auto">
            <Link to="/">На сайт</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}

function validateContent(content) {
  const errors = [];

  const categorySlugs = content.catalog.categories.map((c) => c.slug);
  const productSlugs = content.catalog.products.map((p) => p.slug);

  if (new Set(categorySlugs).size !== categorySlugs.length) {
    errors.push("Slug категорий должны быть уникальны.");
  }
  if (new Set(productSlugs).size !== productSlugs.length) {
    errors.push("Slug товаров должны быть уникальны.");
  }

  const categorySet = new Set(categorySlugs);

  content.catalog.products.forEach((p) => {
    if (!p.title?.trim()) errors.push(`Товар ${p.slug || p.id}: пустой title.`);
    if (Number(p.price) < 0) errors.push(`Товар ${p.slug || p.id}: price < 0.`);
    if (!categorySet.has(p.categorySlug)) {
      errors.push(`Товар ${p.slug || p.id}: неизвестная категория ${p.categorySlug}.`);
    }
    if (p.status === "published") {
      if (!p.images?.length) {
        errors.push(`Published товар ${p.slug || p.id} должен иметь минимум 1 изображение.`);
      }
      (p.images || []).forEach((img) => {
        if (!img.startsWith("/uploads/")) {
          errors.push(`Published товар ${p.slug || p.id}: изображение не с сервера (${img}).`);
        }
      });
    }
  });

  const bannerExtRe = /\.(jpe?g|png|webp)(\?.*)?$/i;
  content.home.slides.forEach((slide, index) => {
    if (!slide.image?.trim()) {
      errors.push(`Слайд #${index + 1}: отсутствует изображение.`);
      return;
    }
    if (!slide.image.startsWith("/uploads/")) {
      errors.push(`Слайд #${index + 1}: изображение должно быть с сервера (${slide.image}).`);
    }
    if (!bannerExtRe.test(slide.image)) {
      errors.push(`Слайд #${index + 1}: поддерживаются только JPG/PNG/WEBP.`);
    }
  });

  return errors;
}

function MetricCard({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 shadow-[0_10px_24px_rgba(31,26,23,0.05)]">
      <div className="text-xs uppercase tracking-[0.12em] text-[#756b5f]">{label}</div>
      <div className="mt-2 text-3xl font-semibold leading-none text-[#231f1a]">{value}</div>
      {hint ? <div className="mt-2 text-xs text-[#756b5f]">{hint}</div> : null}
    </div>
  );
}

function FieldShell({ label, hint, action = null, className = "", children }) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-[#231f1a]">{label}</div>
        {action}
      </div>
      {children}
      {hint ? <div className="text-xs leading-5 text-[#756b5f]">{hint}</div> : null}
    </div>
  );
}

function ProductSection({ title, desc, action = null, children }) {
  return (
    <section className="rounded-xl border border-[#ddd3c4] bg-white p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[#231f1a]">{title}</div>
          {desc ? <div className="mt-1 text-sm leading-6 text-[#5f584f]">{desc}</div> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function MetaChip({ tone = "neutral", children }) {
  const toneClassName = {
    neutral: "border-[#ddd3c4] bg-[#f8f4ed] text-[#5f584f]",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
  };

  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium", toneClassName[tone] || toneClassName.neutral)}>
      {children}
    </span>
  );
}

function getProductIssues(product, categories) {
  if (!product) return [];

  const issues = [];
  const categoryExists = categories.some((category) => category.slug === product.categorySlug);

  if (!product.title?.trim()) {
    issues.push("Не заполнено название товара.");
  }
  if (!product.slug?.trim()) {
    issues.push("Не заполнен slug карточки.");
  }
  if (!categoryExists) {
    issues.push(`Категория "${product.categorySlug || "не выбрана"}" не существует.`);
  }
  if (Number(product.price) < 0) {
    issues.push("Цена не может быть меньше нуля.");
  }
  if (product.status === "published" && !(product.images || []).length) {
    issues.push("Для публикации нужно хотя бы одно изображение.");
  }
  if (product.status === "published") {
    (product.images || []).forEach((imageUrl, index) => {
      if (imageUrl && !imageUrl.startsWith("/uploads/")) {
        issues.push(`Изображение #${index + 1} должно быть загружено на сервер.`);
      }
    });
  }

  return issues;
}

function ProductIssuesPanel({ issues }) {
  if (!issues.length) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        Карточка заполнена корректно и готова к сохранению.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" />
        Что нужно исправить в этой карточке
      </div>
      <div className="mt-3 space-y-2 text-sm text-amber-900">
        {issues.map((issue) => (
          <div key={issue} className="rounded-lg bg-white/60 px-3 py-2">
            {issue}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const { content, update, save, refetch, isLoading } = useContent();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = React.useState("overview");
  const [pagesTab, setPagesTab] = React.useState("home");
  const [isSaving, setIsSaving] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [activeProductId, setActiveProductId] = React.useState("");
  const [activeCategorySlug, setActiveCategorySlug] = React.useState("");

  const lastSavedRef = React.useRef("");

  React.useEffect(() => {
    if (!content.company) {
      update("company", { ...COMPANY_DEFAULTS });
    }
  }, [content.company, update]);

  React.useEffect(() => {
    if (!activeProductId && content.catalog.products.length) {
      setActiveProductId(content.catalog.products[0].id);
    }
    if (!activeCategorySlug && content.catalog.categories.length) {
      setActiveCategorySlug(content.catalog.categories[0].slug);
    }
    if (!lastSavedRef.current && !isLoading) {
      lastSavedRef.current = JSON.stringify(content);
    }
  }, [content, activeProductId, activeCategorySlug, isLoading]);

  const hasUnsavedChanges = React.useMemo(() => {
    if (!lastSavedRef.current) return false;
    return JSON.stringify(content) !== lastSavedRef.current;
  }, [content]);

  React.useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const validationErrors = React.useMemo(() => validateContent(content), [content]);
  const company = content.company || COMPANY_DEFAULTS;
  const activeProduct = content.catalog.products.find((p) => p.id === activeProductId) || null;
  const activeCategory = content.catalog.categories.find((c) => c.slug === activeCategorySlug) || null;
  const categoryTitleMap = React.useMemo(
    () => Object.fromEntries(content.catalog.categories.map((category) => [category.slug, category.title])),
    [content.catalog.categories]
  );

  const filteredProducts = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...content.catalog.products]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((p) => {
        const bySearch = !q || p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q);
        const byCategory = categoryFilter === "all" || p.categorySlug === categoryFilter;
        return bySearch && byCategory;
      });
  }, [content.catalog.products, search, categoryFilter]);
  const productIssuesById = React.useMemo(
    () => Object.fromEntries(content.catalog.products.map((product) => [product.id, getProductIssues(product, content.catalog.categories)])),
    [content.catalog.products, content.catalog.categories]
  );
  const activeProductIssues = activeProduct ? productIssuesById[activeProduct.id] || [] : [];
  const activeProductIndex = React.useMemo(
    () => filteredProducts.findIndex((product) => product.id === activeProductId),
    [filteredProducts, activeProductId]
  );
  const activeProductCategoryTitle = activeProduct ? categoryTitleMap[activeProduct.categorySlug] || activeProduct.categorySlug || "Без категории" : "";

  const publishedProducts = React.useMemo(
    () => content.catalog.products.filter((p) => p.status === "published").length,
    [content.catalog.products]
  );

  const setProducts = (nextProducts) => update("catalog.products", nextProducts);
  const setCategories = (nextCategories) => {
    update("catalog.categories", nextCategories);
    update("home.categories", nextCategories);
  };
  const setSlides = (nextSlides) => update("home.slides", nextSlides);
  const setBenefits = (nextBenefits) => update("home.benefits", nextBenefits);

  const addProduct = () => {
    const id = buildId("p");
    const categorySlug = content.catalog.categories[0]?.slug || "";
    const next = {
      id,
      slug: `novyy-tovar-${id.slice(-4)}`,
      categorySlug,
      title: "Новый товар",
      price: 0,
      priceOld: null,
      currency: "BYN",
      status: "draft",
      order: content.catalog.products.length,
      images: [],
      options: { material: [], size: [] },
      description: "",
      characteristics: [],
    };
    setProducts([next, ...content.catalog.products]);
    setActiveProductId(id);
    setActiveTab("products");
  };

  const duplicateProduct = (id) => {
    const source = content.catalog.products.find((p) => p.id === id);
    if (!source) return;
    const copyId = buildId("p");
    const next = {
      ...structuredClone(source),
      id: copyId,
      slug: `${source.slug}-${copyId.slice(-3)}`,
      title: `${source.title} (копия)`,
      status: "draft",
      order: content.catalog.products.length,
    };
    setProducts([next, ...content.catalog.products]);
    setActiveProductId(copyId);
  };

  const deleteProduct = (id) => {
    const target = content.catalog.products.find((p) => p.id === id);
    if (!target) return;
    if (!window.confirm(`Удалить товар "${target.title}"?`)) return;
    const next = content.catalog.products.filter((p) => p.id !== id);
    setProducts(next);
    if (activeProductId === id) setActiveProductId(next[0]?.id || "");
  };

  const moveProduct = (id, direction) => {
    const index = content.catalog.products.findIndex((p) => p.id === id);
    if (index === -1) return;
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= content.catalog.products.length) return;
    const next = structuredClone(content.catalog.products);
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    next.forEach((p, idx) => {
      p.order = idx;
    });
    setProducts(next);
  };

  const updateProduct = (patch) => {
    const next = structuredClone(content.catalog.products);
    const idx = next.findIndex((p) => p.id === activeProductId);
    if (idx === -1) return;
    next[idx] = { ...next[idx], ...patch };
    setProducts(next);
  };

  const updateProductImage = (index, url) => {
    const imgs = [...(activeProduct?.images || [])];
    imgs[index] = url;
    updateProduct({ images: imgs.filter(Boolean) });
  };

  const addProductImage = () => {
    updateProduct({ images: [...(activeProduct?.images || []), ""] });
  };

  const removeProductImage = (index) => {
    const imgs = [...(activeProduct?.images || [])];
    imgs.splice(index, 1);
    updateProduct({ images: imgs });
  };

  const moveProductImage = (index, direction) => {
    const imgs = [...(activeProduct?.images || [])];
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= imgs.length) return;
    [imgs[index], imgs[swapWith]] = [imgs[swapWith], imgs[index]];
    updateProduct({ images: imgs });
  };

  const updateOptionItem = (optionKey, index, value) => {
    const nextOptions = structuredClone(activeProduct?.options || { material: [], size: [] });
    const list = [...(nextOptions[optionKey] || [])];
    list[index] = value;
    nextOptions[optionKey] = list;
    updateProduct({ options: nextOptions });
  };

  const addOptionItem = (optionKey) => {
    const nextOptions = structuredClone(activeProduct?.options || { material: [], size: [] });
    nextOptions[optionKey] = [...(nextOptions[optionKey] || []), ""];
    updateProduct({ options: nextOptions });
  };

  const removeOptionItem = (optionKey, index) => {
    const nextOptions = structuredClone(activeProduct?.options || { material: [], size: [] });
    const list = [...(nextOptions[optionKey] || [])];
    list.splice(index, 1);
    nextOptions[optionKey] = list;
    updateProduct({ options: nextOptions });
  };

  const updateCharacteristic = (index, patch) => {
    const next = [...(activeProduct?.characteristics || [])];
    next[index] = { ...next[index], ...patch };
    updateProduct({ characteristics: next });
  };

  const addCharacteristic = () => {
    updateProduct({ characteristics: [...(activeProduct?.characteristics || []), { k: "", v: "" }] });
  };

  const removeCharacteristic = (index) => {
    const next = [...(activeProduct?.characteristics || [])];
    next.splice(index, 1);
    updateProduct({ characteristics: next });
  };

  const addCategory = () => {
    const id = buildId("cat");
    const slug = `kategoriya-${id.slice(-4)}`;
    const next = { slug, title: "Новая категория", image: "" };
    const categories = [...content.catalog.categories, next];
    setCategories(categories);
    setActiveCategorySlug(slug);
    setActiveTab("categories");
  };

  const updateCategory = (slug, patch) => {
    const categories = structuredClone(content.catalog.categories).map((c) => (c.slug === slug ? { ...c, ...patch } : c));
    const currentSlug = slug;
    const newSlug = patch.slug || currentSlug;
    setCategories(categories);
    if (newSlug !== currentSlug) {
      const nextProducts = structuredClone(content.catalog.products).map((p) =>
        p.categorySlug === currentSlug ? { ...p, categorySlug: newSlug } : p
      );
      setProducts(nextProducts);
      if (activeCategorySlug === currentSlug) setActiveCategorySlug(newSlug);
    }
  };

  const deleteCategory = (slug) => {
    const inUseBy = content.catalog.products.filter((p) => p.categorySlug === slug);
    if (inUseBy.length > 0) {
      const available = content.catalog.categories.filter((c) => c.slug !== slug);
      if (!available.length) {
        toast({
          variant: "destructive",
          title: "Нельзя удалить",
          description: "Это последняя категория, а к ней привязаны товары.",
        });
        return;
      }
      const moveTo = window.prompt(
        `Категория используется в ${inUseBy.length} товарах. Введите slug категории для переноса:\n${available.map((c) => c.slug).join(", ")}`,
        available[0].slug
      );
      if (!moveTo || !available.some((c) => c.slug === moveTo)) {
        toast({
          variant: "destructive",
          title: "Удаление отменено",
          description: "Неверный slug категории переноса.",
        });
        return;
      }
      const nextProducts = structuredClone(content.catalog.products).map((p) =>
        p.categorySlug === slug ? { ...p, categorySlug: moveTo } : p
      );
      setProducts(nextProducts);
    }
    if (!window.confirm("Удалить категорию?")) return;
    const nextCategories = content.catalog.categories.filter((c) => c.slug !== slug);
    setCategories(nextCategories);
    if (activeCategorySlug === slug) setActiveCategorySlug(nextCategories[0]?.slug || "");
  };

  const addHomeSlide = () => {
    const id = buildId("s");
    const next = [...(content.home.slides || []), { id, title: "Новый слайд", subtitle: "", image: "", tone: "light" }];
    setSlides(next);
  };

  const updateHomeSlide = (index, patch) => {
    const next = structuredClone(content.home.slides || []);
    if (index < 0 || index >= next.length) return;
    next[index] = { ...next[index], ...patch };
    setSlides(next);
  };

  const moveHomeSlide = (index, direction) => {
    const swapWith = direction === "up" ? index - 1 : index + 1;
    const next = structuredClone(content.home.slides || []);
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    setSlides(next);
  };

  const removeHomeSlide = (index) => {
    const target = content.home.slides?.[index];
    if (!target) return;
    if (!window.confirm("Удалить этот слайд?")) return;
    const next = structuredClone(content.home.slides || []);
    next.splice(index, 1);
    setSlides(next);
  };

  const addBenefit = () => {
    const id = buildId("b");
    setBenefits([...(content.home.benefits || []), { id, title: "Новое преимущество", text: "", icon: "" }]);
  };

  const updateBenefit = (id, patch) => {
    const next = structuredClone(content.home.benefits || []).map((b) => (b.id === id ? { ...b, ...patch } : b));
    setBenefits(next);
  };

  const moveBenefit = (index, direction) => {
    const swapWith = direction === "up" ? index - 1 : index + 1;
    const next = structuredClone(content.home.benefits || []);
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    setBenefits(next);
  };

  const removeBenefit = (id) => {
    const next = (content.home.benefits || []).filter((b) => b.id !== id);
    setBenefits(next);
  };

  const addAboutBlock = () => {
    const next = [...(content.pages.about.blocks || []), { type: "text", text: "Новый абзац" }];
    update("pages.about.blocks", next);
  };

  const updateAboutBlock = (index, patch) => {
    const next = structuredClone(content.pages.about.blocks || []);
    next[index] = { ...next[index], ...patch };
    update("pages.about.blocks", next);
  };

  const moveAboutBlock = (index, direction) => {
    const swapWith = direction === "up" ? index - 1 : index + 1;
    const next = structuredClone(content.pages.about.blocks || []);
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[index], next[swapWith]] = [next[swapWith], next[index]];
    update("pages.about.blocks", next);
  };

  const removeAboutBlock = (index) => {
    const next = structuredClone(content.pages.about.blocks || []);
    next.splice(index, 1);
    update("pages.about.blocks", next);
  };

  const addNavItem = () => {
    const next = [...(content.nav || []), { label: "Новый пункт", to: "/new-page" }];
    update("nav", next);
  };

  const updateNavItem = (index, patch) => {
    const next = structuredClone(content.nav || []);
    next[index] = { ...next[index], ...patch };
    update("nav", next);
  };

  const removeNavItem = (index) => {
    const next = structuredClone(content.nav || []);
    next.splice(index, 1);
    update("nav", next);
  };

  const selectRelativeProduct = (direction) => {
    if (activeProductIndex === -1) return;
    const offset = direction === "prev" ? -1 : 1;
    const next = filteredProducts[activeProductIndex + offset];
    if (next) setActiveProductId(next.id);
  };

  const logout = async () => {
    try {
      await axios.post(`${ADMIN_AUTH_API_BASE}/logout`, {}, { withCredentials: true });
    } finally {
      nav("/", { replace: true });
    }
  };

  const handleSave = async () => {
    const errors = validateContent(content);
    if (errors.length) {
      toast({
        variant: "destructive",
        title: "Проверьте данные",
        description: errors[0],
      });
      return;
    }
    setIsSaving(true);
    try {
      await save();
      lastSavedRef.current = JSON.stringify(content);
      toast({ title: "Сохранено", description: "Изменения записаны в базу данных." });
    } catch (error) {
      const message = error?.response?.data?.detail || "Не удалось сохранить изменения.";
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: Array.isArray(message) ? message[0]?.msg || "Ошибка валидации" : String(message),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-zinc-600">Загрузка контента...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      <div className="surface-card sticky top-2 z-20 mb-5 rounded-xl border border-[#ddd3c4] p-3 sm:mb-6 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="eyebrow">Панель управления</div>
            <div className="mt-1 text-2xl font-semibold text-[#231f1a]">DALINI Admin</div>
            <div className="text-sm text-[#5f584f]">
              Управление страницами, подвалом, реквизитами, каталогом, SEO и баннерами. Версия: {content.version}
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
              {isSaving ? "Сохранение..." : "Сохранить изменения"}
            </Button>
            <Button onClick={refetch} variant="secondary" className="w-full sm:w-auto">
              Обновить с сервера
            </Button>
            <Button asChild variant="secondary" className="w-full sm:w-auto">
              <Link to="/">Открыть сайт</Link>
            </Button>
            <Button onClick={logout} variant="secondary" className="w-full sm:w-auto">
              <LogOut className="mr-2 h-4 w-4" />
              Выйти
            </Button>
          </div>
        </div>
        {hasUnsavedChanges ? (
          <div className="mt-3 inline-flex items-center gap-2 rounded bg-amber-50 px-3 py-1 text-xs text-amber-800">
            <FileText className="h-3.5 w-3.5" />
            Есть несохраненные изменения
          </div>
        ) : (
          <div className="mt-3 inline-flex items-center gap-2 rounded bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
            <Check className="h-3.5 w-3.5" />
            Все изменения сохранены
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="surface-card rounded-xl border border-[#ddd3c4] p-3 sm:p-6">
        <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto whitespace-nowrap bg-[#f4ede3] px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsTrigger value="overview" className="gap-2"><Settings className="h-4 w-4" />Обзор</TabsTrigger>
          <TabsTrigger value="products" className="gap-2"><Package className="h-4 w-4" />Товары</TabsTrigger>
          <TabsTrigger value="categories" className="gap-2"><FolderTree className="h-4 w-4" />Категории</TabsTrigger>
          <TabsTrigger value="pages" className="gap-2"><FileText className="h-4 w-4" />Страницы и подвал</TabsTrigger>
          <TabsTrigger value="brand" className="gap-2"><ImageIcon className="h-4 w-4" />Бренд и меню</TabsTrigger>
          <TabsTrigger value="seo" className="gap-2"><Search className="h-4 w-4" />SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <SectionTitle
            title="Быстрый контроль сайта"
            desc="Здесь видно состояние контента и куда перейти для правок. Этот экран нужен для ежедневной работы без лишних кликов."
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <MetricCard label="Товары" value={content.catalog.products.length} />
            <MetricCard label="Опубликовано" value={publishedProducts} />
            <MetricCard label="Категории" value={content.catalog.categories.length} />
            <MetricCard label="Слайды" value={content.home.slides.length} />
            <MetricCard label="Преимущества" value={content.home.benefits.length} />
            <MetricCard label="Пункты меню" value={content.nav.length} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
              <div className="text-sm font-semibold text-[#231f1a]">Ключевые действия</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" variant="secondary" onClick={() => setActiveTab("products")}>Товары</Button>
                <Button type="button" variant="secondary" onClick={() => setActiveTab("pages")}>Страницы и подвал</Button>
                <Button type="button" variant="secondary" onClick={() => setActiveTab("brand")}>Бренд и меню</Button>
                <Button type="button" variant="secondary" onClick={() => setActiveTab("seo")}>SEO</Button>
                <Button type="button" onClick={addProduct}><Plus className="mr-2 h-4 w-4" />Новый товар</Button>
              </div>
            </div>

            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
              <div className="text-sm font-semibold text-[#231f1a]">Проверка перед сохранением</div>
              {validationErrors.length ? (
                <div className="mt-3 space-y-2 text-sm text-amber-800">
                  {validationErrors.slice(0, 4).map((msg) => (
                    <div key={msg} className="rounded bg-amber-50 px-3 py-2">{msg}</div>
                  ))}
                  {validationErrors.length > 4 ? <div className="text-xs text-amber-700">Еще ошибок: {validationErrors.length - 4}</div> : null}
                </div>
              ) : (
                <div className="mt-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Контент проходит валидацию.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionTitle title="Карточки товаров" desc="Добавляйте, редактируйте, дублируйте и публикуйте товары в одном рабочем экране." />
            <Button type="button" onClick={addProduct}><Plus className="mr-2 h-4 w-4" />Добавить товар</Button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px_160px]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <Input className="pl-9" placeholder="Поиск по названию или slug" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="all">Все категории</option>
              {content.catalog.categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.title}</option>
              ))}
            </select>
            <div className="self-center text-sm text-zinc-600">Найдено: {filteredProducts.length}</div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_1fr]">
            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Список товаров</div>
              <div className="max-h-[74vh] space-y-2 overflow-auto">
                {filteredProducts.map((p, index) => {
                  const issues = productIssuesById[p.id] || [];
                  const categoryTitle = categoryTitleMap[p.categorySlug] || p.categorySlug || "Без категории";

                  return (
                    <div
                      key={p.id}
                      className={cn(
                        "rounded-xl border p-3 transition-colors",
                        p.id === activeProductId ? "border-zinc-900 bg-white shadow-[0_12px_24px_rgba(31,26,23,0.08)]" : "border-zinc-200 bg-[#fffaf2] hover:bg-white"
                      )}
                    >
                      <button type="button" onClick={() => setActiveProductId(p.id)} className="w-full text-left">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-[#231f1a]">{p.title || "Без названия"}</div>
                          <div className={cn("rounded px-2 py-0.5 text-[10px] uppercase", p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700")}>
                            {p.status === "published" ? "published" : "draft"}
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">#{index + 1} /product/{p.slug}</div>
                        <div className="mt-1 text-xs text-zinc-500">{p.price} {p.currency}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <MetaChip>{categoryTitle}</MetaChip>
                          <MetaChip>{(p.images || []).length} фото</MetaChip>
                          {issues.length ? <MetaChip tone="warning">Замечаний: {issues.length}</MetaChip> : <MetaChip tone="success">Готово</MetaChip>}
                        </div>
                      </button>
                      <div className="mt-3 flex flex-wrap gap-1">
                        <Button size="sm" variant="secondary" title="Дублировать товар" aria-label="Дублировать товар" onClick={() => duplicateProduct(p.id)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="secondary" title="Поднять выше" aria-label="Поднять товар выше" onClick={() => moveProduct(p.id, "up")}>
                          <MoveUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="secondary" title="Опустить ниже" aria-label="Опустить товар ниже" onClick={() => moveProduct(p.id, "down")}>
                          <MoveDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="destructive" title="Удалить товар" aria-label="Удалить товар" onClick={() => deleteProduct(p.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {!filteredProducts.length ? <div className="text-sm text-zinc-500">Ничего не найдено.</div> : null}
              </div>
            </div>

            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
              {activeProduct ? (
                <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-5">
                    <div className="rounded-xl border border-[#ddd3c4] bg-white p-4">
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">Редактирование товара</div>
                          <div className="mt-1 text-2xl font-semibold text-[#231f1a]">{activeProduct.title || "Без названия"}</div>
                          <div className="mt-1 text-sm text-zinc-500">{activeProduct.slug ? `/product/${activeProduct.slug}` : "Slug еще не задан"}</div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <MetaChip tone={activeProduct.status === "published" ? "success" : "neutral"}>
                              {activeProduct.status === "published" ? "Опубликован" : "Черновик"}
                            </MetaChip>
                            <MetaChip>{activeProductCategoryTitle}</MetaChip>
                            <MetaChip>{(activeProduct.images || []).length} фото</MetaChip>
                            {activeProductIndex !== -1 ? <MetaChip>{activeProductIndex + 1} из {filteredProducts.length}</MetaChip> : null}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="secondary" onClick={() => selectRelativeProduct("prev")} disabled={activeProductIndex <= 0}>
                            <ChevronLeft className="mr-2 h-4 w-4" />
                            Предыдущий
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => selectRelativeProduct("next")}
                            disabled={activeProductIndex === -1 || activeProductIndex >= filteredProducts.length - 1}
                          >
                            Следующий
                            <ChevronRight className="ml-2 h-4 w-4" />
                          </Button>
                          <Button type="button" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Сохранение..." : "Сохранить"}
                          </Button>
                          {activeProduct.slug ? (
                            <Button asChild type="button" variant="secondary">
                              <Link to={`/product/${activeProduct.slug}`} target="_blank" rel="noreferrer">
                                Открыть
                                <ExternalLink className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          ) : null}
                          <Button type="button" variant="secondary" onClick={() => duplicateProduct(activeProduct.id)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Дублировать
                          </Button>
                          <Button
                            type="button"
                            variant={activeProduct.status === "published" ? "default" : "secondary"}
                            onClick={() => updateProduct({ status: activeProduct.status === "published" ? "draft" : "published" })}
                          >
                            {activeProduct.status === "published" ? "Снять с публикации" : "Опубликовать"}
                          </Button>
                          <Button type="button" variant="destructive" onClick={() => deleteProduct(activeProduct.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Удалить
                          </Button>
                        </div>
                      </div>
                    </div>

                    <ProductIssuesPanel issues={activeProductIssues} />

                    <ProductSection title="Основное" desc="Ключевые поля карточки: название, URL, раздел и цены.">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        <FieldShell label="Название товара" hint="Название показывается в каталоге и в карточке товара.">
                          <Input value={activeProduct.title} onChange={(e) => updateProduct({ title: e.target.value })} placeholder="Например, Montana" />
                        </FieldShell>

                        <FieldShell
                          label="Slug карточки"
                          hint="Используется в адресе страницы товара."
                          action={
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => updateProduct({ slug: slugify(activeProduct.title) })}
                              disabled={!activeProduct.title?.trim()}
                            >
                              Из названия
                            </Button>
                          }
                        >
                          <Input value={activeProduct.slug} onChange={(e) => updateProduct({ slug: slugify(e.target.value) })} placeholder="slug" />
                        </FieldShell>

                        <FieldShell label="Категория" hint="В этом разделе товар появится в каталоге сайта.">
                          <select
                            value={activeProduct.categorySlug}
                            onChange={(e) => updateProduct({ categorySlug: e.target.value })}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          >
                            {content.catalog.categories.map((c) => (
                              <option key={c.slug} value={c.slug}>{c.title} ({c.slug})</option>
                            ))}
                          </select>
                        </FieldShell>

                        <FieldShell label="Валюта" hint="Короткое обозначение рядом с ценой.">
                          <Input value={activeProduct.currency} onChange={(e) => updateProduct({ currency: e.target.value })} placeholder="BYN" />
                        </FieldShell>

                        <FieldShell label="Цена" hint="Текущая цена товара.">
                          <Input value={activeProduct.price} onChange={(e) => updateProduct({ price: Number(e.target.value || 0) })} placeholder="Цена" type="number" min={0} />
                        </FieldShell>

                        <FieldShell label="Старая цена" hint="Оставьте пустым, если скидки нет.">
                          <Input
                            value={activeProduct.priceOld || ""}
                            onChange={(e) => updateProduct({ priceOld: e.target.value ? Number(e.target.value) : null })}
                            placeholder="Старая цена"
                            type="number"
                            min={0}
                          />
                        </FieldShell>
                      </div>
                    </ProductSection>

                    <ProductSection title="Описание" desc="Полное описание карточки, размеры, условия изготовления и важные детали.">
                      <FieldShell label="Текст описания" hint="Текст можно вставлять с переносами строк, они сохранятся.">
                        <Textarea value={activeProduct.description || ""} onChange={(e) => updateProduct({ description: e.target.value })} rows={8} />
                      </FieldShell>
                    </ProductSection>

                    <ProductSection
                      title="Изображения"
                      desc="Первое изображение используется как основное в каталоге и на карточке товара."
                      action={
                        <Button type="button" variant="secondary" onClick={addProductImage}>
                          <Plus className="mr-2 h-4 w-4" />
                          Добавить изображение
                        </Button>
                      }
                    >
                      <div className="space-y-3">
                        {(activeProduct.images || []).map((url, idx) => (
                          <div key={`${activeProduct.id}-img-${idx}`} className="rounded-xl border border-zinc-200 bg-[#fffdf8] p-3">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                                Изображение #{idx + 1}{idx === 0 ? " (основное)" : ""}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => moveProductImage(idx, "up")}
                                  disabled={idx === 0}
                                  title="Сдвинуть изображение выше"
                                  aria-label="Сдвинуть изображение выше"
                                >
                                  <MoveUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => moveProductImage(idx, "down")}
                                  disabled={idx === (activeProduct.images || []).length - 1}
                                  title="Сдвинуть изображение ниже"
                                  aria-label="Сдвинуть изображение ниже"
                                >
                                  <MoveDown className="h-3.5 w-3.5" />
                                </Button>
                                <Button type="button" variant="destructive" size="sm" onClick={() => removeProductImage(idx)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_112px]">
                              <ImagePicker value={url} onChange={(val) => updateProductImage(idx, val)} placeholder={`Изображение #${idx + 1}`} />
                              {url ? (
                                <img src={url} alt="" className="h-28 w-28 rounded border object-cover" />
                              ) : (
                                <div className="flex h-28 w-28 items-center justify-center rounded border border-dashed text-xs text-zinc-500">
                                  Нет файла
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {!activeProduct.images?.length ? (
                          <div className="rounded-xl border border-dashed border-[#d8cebf] bg-[#fffcf6] px-4 py-6 text-sm text-zinc-500">
                            Добавьте минимум одно изображение, если товар нужно публиковать.
                          </div>
                        ) : null}
                      </div>
                    </ProductSection>

                    <ProductSection title="Опции" desc="Материалы и размеры, которые показываются клиенту на сайте.">
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <div className="rounded-xl border border-zinc-200 bg-[#fffdf8] p-3">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-[#231f1a]">Материалы</div>
                            <Button type="button" size="sm" variant="secondary" onClick={() => addOptionItem("material")}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(activeProduct.options?.material || []).map((item, idx) => (
                              <div key={`mat-${idx}`} className="flex gap-2">
                                <Input value={item} onChange={(e) => updateOptionItem("material", idx, e.target.value)} placeholder="Например, массив дуба" />
                                <Button type="button" size="sm" variant="destructive" onClick={() => removeOptionItem("material", idx)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ))}
                            {!activeProduct.options?.material?.length ? <div className="text-sm text-zinc-500">Материалы пока не добавлены.</div> : null}
                          </div>
                        </div>

                        <div className="rounded-xl border border-zinc-200 bg-[#fffdf8] p-3">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-[#231f1a]">Размеры</div>
                            <Button type="button" size="sm" variant="secondary" onClick={() => addOptionItem("size")}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(activeProduct.options?.size || []).map((item, idx) => (
                              <div key={`size-${idx}`} className="flex gap-2">
                                <Input value={item} onChange={(e) => updateOptionItem("size", idx, e.target.value)} placeholder="Например, 900x1300 мм" />
                                <Button type="button" size="sm" variant="destructive" onClick={() => removeOptionItem("size", idx)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ))}
                            {!activeProduct.options?.size?.length ? <div className="text-sm text-zinc-500">Размеры пока не добавлены.</div> : null}
                          </div>
                        </div>
                      </div>
                    </ProductSection>

                    <ProductSection
                      title="Характеристики"
                      desc="Пары параметр/значение для подробной карточки товара."
                      action={
                        <Button type="button" variant="secondary" size="sm" onClick={addCharacteristic}>
                          <Plus className="mr-2 h-3.5 w-3.5" />
                          Добавить характеристику
                        </Button>
                      }
                    >
                      <div className="space-y-2">
                        {(activeProduct.characteristics || []).map((row, idx) => (
                          <div key={`char-${idx}`} className="grid grid-cols-1 gap-2 rounded-xl border border-zinc-200 bg-[#fffdf8] p-3 sm:grid-cols-[1fr_1fr_auto]">
                            <Input value={row.k || ""} onChange={(e) => updateCharacteristic(idx, { k: e.target.value })} placeholder="Параметр" />
                            <Input value={row.v || ""} onChange={(e) => updateCharacteristic(idx, { v: e.target.value })} placeholder="Значение" />
                            <Button type="button" size="sm" variant="destructive" onClick={() => removeCharacteristic(idx)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                        {!activeProduct.characteristics?.length ? <div className="text-sm text-zinc-500">Характеристики пока не добавлены.</div> : null}
                      </div>
                    </ProductSection>
                  </div>

                  <div className="space-y-4 self-start 2xl:sticky 2xl:top-6">
                    <div className="rounded-xl border border-[#ddd3c4] bg-white p-4">
                      <div className="mb-2 text-xs uppercase tracking-[0.12em] text-zinc-500">Предпросмотр карточки</div>
                      {activeProduct.images?.[0] ? (
                        <img src={activeProduct.images[0]} alt="" className="h-56 w-full rounded border object-cover" />
                      ) : (
                        <div className="flex h-56 items-center justify-center rounded border border-dashed text-sm text-zinc-500">Нет изображения</div>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <MetaChip tone={activeProduct.status === "published" ? "success" : "neutral"}>
                          {activeProduct.status === "published" ? "Опубликован" : "Черновик"}
                        </MetaChip>
                        <MetaChip>{activeProductCategoryTitle}</MetaChip>
                      </div>
                      <div className="mt-3 text-lg font-semibold text-[#231f1a]">{activeProduct.title || "Без названия"}</div>
                      <div className="mt-1 text-sm text-zinc-600">{activeProduct.price} {activeProduct.currency}</div>
                      <div className="mt-2 text-xs text-zinc-500">/product/{activeProduct.slug}</div>
                      {activeProduct.description?.trim() ? (
                        <div className="mt-3 text-sm leading-6 text-zinc-600">
                          {activeProduct.description.length > 220 ? `${activeProduct.description.slice(0, 220)}...` : activeProduct.description}
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-zinc-500">Описание пока не заполнено.</div>
                      )}
                    </div>

                    <div className="rounded-xl border border-[#ddd3c4] bg-white p-4">
                      <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">Сводка по карточке</div>
                      <div className="mt-3 space-y-3 text-sm text-zinc-600">
                        <div className="flex items-center justify-between gap-3">
                          <span>Категория</span>
                          <span className="font-medium text-[#231f1a]">{activeProductCategoryTitle}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Изображений</span>
                          <span className="font-medium text-[#231f1a]">{(activeProduct.images || []).length}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Материалов</span>
                          <span className="font-medium text-[#231f1a]">{(activeProduct.options?.material || []).length}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Размеров</span>
                          <span className="font-medium text-[#231f1a]">{(activeProduct.options?.size || []).length}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Характеристик</span>
                          <span className="font-medium text-[#231f1a]">{(activeProduct.characteristics || []).length}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between gap-3">
                          <span>Состояние</span>
                          {activeProductIssues.length ? <MetaChip tone="warning">Нужно исправить: {activeProductIssues.length}</MetaChip> : <MetaChip tone="success">Карточка в порядке</MetaChip>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-600">Выберите товар слева.</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionTitle title="Категории" desc="Категории автоматически синхронизируются с главной страницей." />
            <Button type="button" onClick={addCategory}><Plus className="mr-2 h-4 w-4" />Добавить категорию</Button>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[330px_1fr]">
            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-3">
              <div className="max-h-[70vh] space-y-2 overflow-auto">
                {content.catalog.categories.map((c) => {
                  const count = content.catalog.products.filter((p) => p.categorySlug === c.slug).length;
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setActiveCategorySlug(c.slug)}
                      className={cn(
                        "w-full rounded border px-3 py-2 text-left transition-colors",
                        c.slug === activeCategorySlug ? "border-zinc-900 bg-white" : "border-zinc-200 hover:bg-zinc-50"
                      )}
                    >
                      <div className="text-sm font-semibold">{c.title}</div>
                      <div className="text-xs text-zinc-500">{c.slug}</div>
                      <div className="mt-1 text-xs text-zinc-500">Товаров: {count}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
              {activeCategory ? (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_240px]">
                  <div className="space-y-4">
                    <Input value={activeCategory.title} onChange={(e) => updateCategory(activeCategory.slug, { title: e.target.value })} placeholder="Название категории" />
                    <Input value={activeCategory.slug} onChange={(e) => updateCategory(activeCategory.slug, { slug: slugify(e.target.value) })} placeholder="slug категории" />
                    <ImagePicker value={activeCategory.image} onChange={(url) => updateCategory(activeCategory.slug, { image: url })} placeholder="Изображение категории" />
                    <Button type="button" variant="destructive" onClick={() => deleteCategory(activeCategory.slug)}>
                      <Trash2 className="mr-2 h-4 w-4" />Удалить категорию
                    </Button>
                  </div>
                  <div>
                    {activeCategory.image ? (
                      <img src={activeCategory.image} alt="" className="h-44 w-full rounded border object-cover" />
                    ) : (
                      <div className="flex h-44 items-center justify-center rounded border border-dashed text-sm text-zinc-500">Нет изображения</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-600">Выберите категорию слева.</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pages" className="mt-6 space-y-4">
          <SectionTitle title="Страницы, подвал и реквизиты" desc="Все ключевые тексты и блоки сайта в одном месте: главная, о компании, контакты, подвал." />

          <Tabs value={pagesTab} onValueChange={setPagesTab}>
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto whitespace-nowrap bg-[#f4ede3] px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsTrigger value="home">Главная</TabsTrigger>
              <TabsTrigger value="about">О компании</TabsTrigger>
              <TabsTrigger value="contacts">Контакты и реквизиты</TabsTrigger>
              <TabsTrigger value="footer">Подвал и CTA</TabsTrigger>
            </TabsList>

            <TabsContent value="home" className="mt-4 space-y-6">
              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold">Баннер главной страницы</div>
                  <Button type="button" onClick={addHomeSlide}><Plus className="mr-2 h-4 w-4" />Добавить слайд</Button>
                </div>
                <div className="mb-4 rounded border border-dashed border-[#d8cebf] bg-[#fffcf6] p-3 text-xs leading-5 text-[#5f584f]">
                  Требования к изображению: {HERO_IMAGE_SPEC.width}x{HERO_IMAGE_SPEC.height}px ({HERO_IMAGE_SPEC.ratio}), формат {HERO_IMAGE_SPEC.formats}, только <code>/uploads/...</code>.
                </div>
                <div className="space-y-3">
                  {(content.home.slides || []).map((s, idx) => (
                    <div key={s.id} className="rounded border border-zinc-200 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">Слайд #{idx + 1}</div>
                        <div className="flex items-center gap-2">
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveHomeSlide(idx, "up")} disabled={idx === 0}><MoveUp className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveHomeSlide(idx, "down")} disabled={idx === content.home.slides.length - 1}><MoveDown className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => removeHomeSlide(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Input value={s.title || ""} onChange={(e) => updateHomeSlide(idx, { title: e.target.value })} placeholder="Заголовок" />
                        <Input value={s.subtitle || ""} onChange={(e) => updateHomeSlide(idx, { subtitle: e.target.value })} placeholder="Подзаголовок" />
                        <ImagePicker value={s.image || ""} onChange={(url) => updateHomeSlide(idx, { image: url })} placeholder="Изображение слайда" />
                        <SlideImageMeta url={s.image} />
                        {s.image ? <img src={s.image} alt="" className="aspect-video w-full rounded border object-cover" /> : null}
                      </div>
                    </div>
                  ))}
                  {!content.home.slides?.length ? <div className="text-sm text-zinc-500">Слайдов пока нет.</div> : null}
                </div>
              </div>

              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">Преимущества на главной</div>
                  <Button type="button" variant="secondary" onClick={addBenefit}><Plus className="mr-2 h-4 w-4" />Добавить</Button>
                </div>
                <div className="space-y-3">
                  {(content.home.benefits || []).map((b, idx) => (
                    <div key={b.id} className="rounded border border-zinc-200 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">Преимущество #{idx + 1}</div>
                        <div className="flex items-center gap-2">
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveBenefit(idx, "up")} disabled={idx === 0}><MoveUp className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveBenefit(idx, "down")} disabled={idx === content.home.benefits.length - 1}><MoveDown className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => removeBenefit(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Input value={b.title || ""} onChange={(e) => updateBenefit(b.id, { title: e.target.value })} placeholder="Заголовок" />
                        <Textarea value={b.text || ""} onChange={(e) => updateBenefit(b.id, { text: e.target.value })} rows={3} placeholder="Текст" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="about" className="mt-4 space-y-4">
              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
                <div className="mb-3 text-sm font-semibold">Страница «О компании»</div>
                <Input
                  value={content.pages.about.title || ""}
                  onChange={(e) => update("pages.about.title", e.target.value)}
                  placeholder="Заголовок страницы"
                />
                <div className="mt-4 space-y-3">
                  {(content.pages.about.blocks || []).map((b, idx) => (
                    <div key={`about-${idx}`} className="rounded border border-zinc-200 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">Абзац #{idx + 1}</div>
                        <div className="flex items-center gap-2">
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveAboutBlock(idx, "up")} disabled={idx === 0}><MoveUp className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="secondary" onClick={() => moveAboutBlock(idx, "down")} disabled={idx === content.pages.about.blocks.length - 1}><MoveDown className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => removeAboutBlock(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                      <Textarea value={b.text || ""} onChange={(e) => updateAboutBlock(idx, { text: e.target.value })} rows={4} />
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Button type="button" variant="secondary" onClick={addAboutBlock}><Plus className="mr-2 h-4 w-4" />Добавить абзац</Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
                <div className="text-sm font-semibold">Контактные данные сайта</div>
                <Input value={content.contacts.phone || ""} onChange={(e) => update("contacts.phone", e.target.value)} placeholder="Телефон" />
                <Input value={content.contacts.address || ""} onChange={(e) => update("contacts.address", e.target.value)} placeholder="Адрес" />
                <Textarea value={content.contacts.hours || ""} onChange={(e) => update("contacts.hours", e.target.value)} placeholder="Режим работы" rows={3} />
              </div>

              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
                <div className="text-sm font-semibold">Реквизиты компании</div>
                <Input value={company.legalName || ""} onChange={(e) => update("company.legalName", e.target.value)} placeholder="Юридическое наименование" />
                <Input value={company.address || ""} onChange={(e) => update("company.address", e.target.value)} placeholder="Юридический адрес" />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input value={company.unp || ""} onChange={(e) => update("company.unp", e.target.value)} placeholder="УНП" />
                  <Input value={company.okpo || ""} onChange={(e) => update("company.okpo", e.target.value)} placeholder="ОКПО" />
                </div>
                <Input value={company.iban || ""} onChange={(e) => update("company.iban", e.target.value)} placeholder="IBAN" />
                <Textarea value={company.bank || ""} onChange={(e) => update("company.bank", e.target.value)} placeholder="Банк" rows={2} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input value={company.swift || ""} onChange={(e) => update("company.swift", e.target.value)} placeholder="SWIFT" />
                  <Input value={company.director || ""} onChange={(e) => update("company.director", e.target.value)} placeholder="Директор" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input value={company.email || ""} onChange={(e) => update("company.email", e.target.value)} placeholder="Email" />
                  <Input value={company.phone || ""} onChange={(e) => update("company.phone", e.target.value)} placeholder="Телефон компании" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="footer" className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
                <div className="text-sm font-semibold">Тексты подвала и кнопки связи</div>
                <Input value={content.uiTexts.footerContactsTitle || ""} onChange={(e) => update("uiTexts.footerContactsTitle", e.target.value)} placeholder="Заголовок блока Контакты" />
                <Input value={content.uiTexts.footerMessageTitle || ""} onChange={(e) => update("uiTexts.footerMessageTitle", e.target.value)} placeholder="Заголовок блока Сообщение" />
                <Textarea value={content.uiTexts.footerMessageBody || ""} onChange={(e) => update("uiTexts.footerMessageBody", e.target.value)} placeholder="Текст сообщения в подвале" rows={3} />
                <Input value={content.uiTexts.contactCtaLabel || ""} onChange={(e) => update("uiTexts.contactCtaLabel", e.target.value)} placeholder="Текст кнопки связи" />
              </div>
              <div className="rounded-xl border border-[#ddd3c4] bg-[#171412] p-4 text-zinc-100">
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-400">Live Preview</div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="font-semibold">{content.brand.name || "DALINI"}</div>
                  <div className="text-zinc-300">{content.contacts.address}</div>
                  <div className="text-zinc-400">УНП {company.unp}</div>
                  <Separator className="my-3 bg-zinc-700" />
                  <div className="text-xs uppercase tracking-[0.12em] text-zinc-400">{content.uiTexts.footerContactsTitle || "Контакты"}</div>
                  <div>{content.contacts.phone}</div>
                  <div className="text-zinc-400">{content.contacts.hours}</div>
                  <Separator className="my-3 bg-zinc-700" />
                  <div className="text-xs uppercase tracking-[0.12em] text-zinc-400">{content.uiTexts.footerMessageTitle || "Сообщение"}</div>
                  <div className="text-zinc-300">{content.uiTexts.footerMessageBody}</div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="brand" className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
            <SectionTitle title="Бренд и шапка сайта" desc="Управляйте логотипом, названием и пунктами меню." />
            <Input value={content.brand.name || ""} onChange={(e) => update("brand.name", e.target.value)} placeholder="Название бренда" />
            <ImagePicker value={content.brand.logoUrl || ""} onChange={(url) => update("brand.logoUrl", url)} placeholder="Логотип" />
            <div className="pt-1">
              <ImageManager trigger={<Button type="button" variant="secondary"><ImageIcon className="mr-2 h-4 w-4" />Медиабиблиотека</Button>} />
            </div>
            {content.brand.logoUrl ? <img src={content.brand.logoUrl} alt="" className="h-16 rounded border object-contain bg-white px-2" /> : null}
          </div>

          <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Пункты меню</div>
              <Button type="button" size="sm" variant="secondary" onClick={addNavItem}><Plus className="h-3.5 w-3.5" /></Button>
            </div>
            {(content.nav || []).map((item, idx) => (
              <div key={`nav-${idx}`} className="rounded border border-zinc-200 p-3 space-y-2">
                <Input value={item.label || ""} onChange={(e) => updateNavItem(idx, { label: e.target.value })} placeholder="Название пункта меню" />
                <div className="flex gap-2">
                  <Input value={item.to || ""} onChange={(e) => updateNavItem(idx, { to: e.target.value })} placeholder="URL, например /catalog" />
                  <Button type="button" size="sm" variant="destructive" onClick={() => removeNavItem(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}

            <Separator className="my-2" />
            <div className="text-sm font-semibold">Служебные тексты интерфейса</div>
            <Input value={content.uiTexts.searchButtonAria || ""} onChange={(e) => update("uiTexts.searchButtonAria", e.target.value)} placeholder="Поиск: aria-label" />
            <Input value={content.uiTexts.catalogTitle || ""} onChange={(e) => update("uiTexts.catalogTitle", e.target.value)} placeholder="Заголовок каталога" />
            <Input value={content.uiTexts.contactPageTitle || ""} onChange={(e) => update("uiTexts.contactPageTitle", e.target.value)} placeholder="Заголовок страницы контактов" />
            <Input value={content.uiTexts.aboutPageTitleFallback || ""} onChange={(e) => update("uiTexts.aboutPageTitleFallback", e.target.value)} placeholder="Fallback заголовка страницы о компании" />
            <Input value={content.uiTexts.notFoundTitle || ""} onChange={(e) => update("uiTexts.notFoundTitle", e.target.value)} placeholder="Заголовок 404" />
            <Textarea value={content.uiTexts.notFoundDescription || ""} onChange={(e) => update("uiTexts.notFoundDescription", e.target.value)} placeholder="Описание 404" rows={2} />
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-6 space-y-6">
          <SectionTitle title="SEO" desc="Title, description, canonical, OG для страниц и карточек товаров." />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-3">
              <div className="text-sm font-semibold">Глобальные настройки SEO</div>
              <Input value={content.seo.siteName || ""} onChange={(e) => update("seo.siteName", e.target.value)} placeholder="Site name" />
              <Input value={content.seo.defaultTitle || ""} onChange={(e) => update("seo.defaultTitle", e.target.value)} placeholder="Default title" />
              <Textarea value={content.seo.defaultDescription || ""} onChange={(e) => update("seo.defaultDescription", e.target.value)} placeholder="Default description" rows={4} />
              <Input value={content.seo.canonicalBase || ""} onChange={(e) => update("seo.canonicalBase", e.target.value)} placeholder="Canonical base (https://dalini.by)" />
              <ImagePicker value={content.seo.defaultOgImage || ""} onChange={(url) => update("seo.defaultOgImage", url)} placeholder="Default OG image" />
            </div>

            <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4 space-y-4">
              <div className="text-sm font-semibold">SEO по маршрутам</div>
              {Object.entries(content.seo.routes || {}).map(([path, val]) => (
                <div key={path} className="rounded border border-zinc-200 p-3 space-y-2">
                  <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">{path}</div>
                  <Input
                    value={val.title || ""}
                    onChange={(e) => {
                      const next = structuredClone(content.seo.routes || {});
                      next[path] = { ...(next[path] || {}), title: e.target.value };
                      update("seo.routes", next);
                    }}
                    placeholder="Title"
                  />
                  <Textarea
                    value={val.description || ""}
                    onChange={(e) => {
                      const next = structuredClone(content.seo.routes || {});
                      next[path] = { ...(next[path] || {}), description: e.target.value };
                      update("seo.routes", next);
                    }}
                    placeholder="Description"
                    rows={2}
                  />
                  <ImagePicker
                    value={val.ogImage || ""}
                    onChange={(url) => {
                      const next = structuredClone(content.seo.routes || {});
                      next[path] = { ...(next[path] || {}), ogImage: url };
                      update("seo.routes", next);
                    }}
                    placeholder="OG image"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-[#ddd3c4] bg-[#fffdf8] p-4">
            <div className="mb-3 text-sm font-semibold">SEO для товаров</div>
            <div className="space-y-3">
              {content.catalog.products.map((p) => {
                const seo = content.seo.products?.[p.slug] || {};
                return (
                  <div key={p.id} className="grid grid-cols-1 gap-2 rounded border border-zinc-200 p-3 md:grid-cols-[220px_1fr_1fr_1fr]">
                    <div className="self-center text-sm font-medium">{p.title}</div>
                    <Input
                      value={seo.title || ""}
                      onChange={(e) => {
                        const next = structuredClone(content.seo.products || {});
                        next[p.slug] = { ...(next[p.slug] || {}), title: e.target.value };
                        update("seo.products", next);
                      }}
                      placeholder="SEO title"
                    />
                    <Input
                      value={seo.description || ""}
                      onChange={(e) => {
                        const next = structuredClone(content.seo.products || {});
                        next[p.slug] = { ...(next[p.slug] || {}), description: e.target.value };
                        update("seo.products", next);
                      }}
                      placeholder="SEO description"
                    />
                    <ImagePicker
                      value={seo.ogImage || ""}
                      onChange={(url) => {
                        const next = structuredClone(content.seo.products || {});
                        next[p.slug] = { ...(next[p.slug] || {}), ogImage: url };
                        update("seo.products", next);
                      }}
                      placeholder="SEO OG image"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
